import { BookOpen, Languages, Library as LibraryIcon, Pencil, Trash2 } from 'lucide-react'
import { gradientFor } from '@/components/library/covers'
import type { Library } from '@/types'
import { spaLink } from '@/lib/spaLink'

/**
 * Collection card for the Library catalog + home row. An uploaded thumbnail
 * if set, otherwise a multi-hue gradient with the Library icon centered;
 * title overlaid; course/language chips below.
 */
export function LibraryCard({
  library, courseCount, languageCount, onClick, onEdit, onDelete, href,
}: {
  library: Library
  courseCount: number
  languageCount: number
  onClick: () => void
  /** Where it opens — makes the card a real link (new tab on Cmd/Ctrl/middle-click). */
  href?: string
  onEdit?: () => void
  onDelete?: () => void
}) {
  return (
    <div className="group relative bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:border-gray-300 transition-all">
      <Opener href={href} onClick={onClick} className="block w-full text-left">
        <div className="relative aspect-video overflow-hidden">
          {library.thumbnail_url ? (
            <img src={library.thumbnail_url} alt={library.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <>
              <div className={`absolute inset-0 bg-gradient-to-br ${gradientFor(library.id)}`} />
              {/* Library icon centered — only when there's no cover image */}
              <LibraryIcon className="absolute inset-0 m-auto w-12 h-12 text-white/20" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2">{library.title}</h3>
          </div>
        </div>
        <div className="p-3">
          {library.description && (
            <p className="text-xs text-gray-500 line-clamp-1">{library.description}</p>
          )}
          <div className="mt-2 flex items-center gap-1.5">
            <Chip><BookOpen className="w-3 h-3" /> {courseCount} course{courseCount !== 1 ? 's' : ''}</Chip>
            {languageCount > 1 && (
              <Chip><Languages className="w-3 h-3" /> Multilingual</Chip>
            )}
          </div>
        </div>
      </Opener>

      {(onEdit || onDelete) && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit() }} className="flex items-center justify-center w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-md" aria-label="Edit library">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button onClick={e => { e.stopPropagation(); onDelete() }} className="flex items-center justify-center w-6 h-6 bg-black/50 hover:bg-red-600 text-white rounded-md" aria-label="Delete library">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** A real link when there is somewhere to open; the old button otherwise. */
function Opener({ href, onClick, className, children }: { href?: string; onClick: () => void; className: string; children: React.ReactNode }) {
  return href
    ? <a {...spaLink(href, onClick)} className={className}>{children}</a>
    : <button onClick={onClick} className={className}>{children}</button>
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-gray-200 text-[11px] text-gray-500">
      {children}
    </span>
  )
}
